import * as Sentry from '@sentry/react';
import axios from 'axios';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import { useConfig } from '../../config';
import { useScript } from '../../hooks/useScript';
import { useTranslation } from '../../i18n/i18n';
import { MessagesContainer } from '../../state/messages';
import { useSession } from '../../state/session';
import { ApiActionResponse } from '../../types';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import { ContentCard } from '../ContentCard/ContentCard';
import { Button } from '../Form/Button';
import { NewPasswordModel } from '../Form/NewPasswordInput';
import { onKeyPress } from '../Form/PressedEnter';
import { TextInput } from '../Form/TextInput';
import { Main } from '../Main/Main';
import { Routing } from '../Routes';
import { renderTemplate } from '../utils';
import './CreateAccountPage.css';

declare global {
	interface Window {
		grecaptcha: {
			ready(action: () => void): void;
			execute(key: string, arg: { action: string }): Promise<string>;
		};
	}
}

export const CreateAccountPage = (): JSX.Element => {
	const { api, reCaptchaPubKey, customerServiceEmail } = useConfig();
	const t = {
		...useTranslation().registrationPage,
		...useTranslation().form,
		...useTranslation().generic,
	};
	const tRoutes = useTranslation().routes;
	const history = useHistory();
	const { login } = useSession();
	const globalAlerts = MessagesContainer.useContainer();
	const [passwordModel, setPasswordModel] = useState<NewPasswordModel>({
		newPassword: '',
		newPassword2: '',
		isValid: false,
	});
	const [email, setEmail] = useState('');
	const [submitted, setSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);
	const [focused, setFocused] = useState('');
	const [touched, setTouched] = useState({ email: false, password: false, confirm: false });
	const [hasMounted, setHasMounted] = useState(false);

	useEffect(() => {
		setHasMounted(true);
		return () => setHasMounted(false);
	}, []);

	const alerts = useAlerts();

	const { newPassword, newPassword2 } = passwordModel;

	const emailPattern = /^(.+)@(.+)$/;

	const longEnough = (password: string) => password.length >= 6;
	const confirmed = (p1: string, p2: string) => p1 === p2;
	const isValid = (p1: string, p2: string) => longEnough(p1) && confirmed(p1, p2);
	const isEmailValid = (email: string) => email.match(emailPattern);
	const onChange = (data: any) => setPasswordModel(data);
	const registrationErrorMessage = renderTemplate(t.somethingWentWrong, {
		customerServiceEmail,
	});
	const recaptchaScriptUrl = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(reCaptchaPubKey)}`;
	const recaptchaScriptStatus = useScript(recaptchaScriptUrl);
	const recaptchaUnavailableMessage = 'Could not verify reCAPTCHA. Please try again.';
	const recaptchaTimeoutMs = 8000;

	function reportRecaptchaFailure(phase: 'script_load' | 'execute' | 'timeout', error: unknown) {
		Sentry.withScope((scope) => {
			scope.setTag('captcha_phase', phase);
			scope.setTag('feature', 'register_account');
			scope.setExtra('recaptcha_script_status', recaptchaScriptStatus);
			Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
		});
	}

	const executeRecaptcha = (action: string) =>
		new Promise<string>((resolve, reject) => {
			if (recaptchaScriptStatus !== 'ready') {
				const error = new Error(`reCAPTCHA script not ready: ${recaptchaScriptStatus}`);
				reportRecaptchaFailure('script_load', error);
				reject(error);
				return;
			}

			if (!window.grecaptcha) {
				const error = new Error('reCAPTCHA is not available');
				reportRecaptchaFailure('script_load', error);
				reject(error);
				return;
			}

			try {
				const timeout = new Promise<never>((_resolve, timeoutReject) => {
					window.setTimeout(() => {
						const timeoutError = new Error(`reCAPTCHA execution timed out after ${recaptchaTimeoutMs}ms`);
						reportRecaptchaFailure('timeout', timeoutError);
						timeoutReject(timeoutError);
					}, recaptchaTimeoutMs);
				});

				const execute = new Promise<string>((executeResolve, executeReject) => {
					window.grecaptcha.ready(async () => {
						try {
							const token = await window.grecaptcha.execute(reCaptchaPubKey, { action });
							if (!token) {
								const missingTokenError = new Error('Missing reCAPTCHA token');
								reportRecaptchaFailure('execute', missingTokenError);
								executeReject(missingTokenError);
								return;
							}
							executeResolve(token);
						} catch (error) {
							reportRecaptchaFailure('execute', error);
							executeReject(error);
						}
					});
				});

				Promise.race([execute, timeout]).then(resolve).catch(reject);
			} catch (error) {
				reportRecaptchaFailure('execute', error);
				reject(error);
			}
		});

	const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setSubmitted(true);
		setLoading(true);

		if (!isEmailValid(email) || !longEnough(newPassword) || !confirmed(newPassword, newPassword2)) {
			setLoading(false);
			return;
		}

		const clearState = () => {
			setPasswordModel({
				newPassword: '',
				newPassword2: '',
				isValid: false,
			});
			setEmail('');
			alerts.clear();
		};

		try {
			const recaptchaToken = await executeRecaptcha('registerAccount');
			const payload = {
				recaptchaToken,
				email,
				password: passwordModel.newPassword,
				confirm: passwordModel.newPassword2,
			};

			const data = (await axios.post<ApiActionResponse>(`${api}register-account.json`, payload)).data;

			if (data.errors) {
				alerts.set({ type: 'error', message: data.errors.join(' ') });
			} else if (data.success) {
				const { success } = await login(email, passwordModel.newPassword);

				if (!success) {
					globalAlerts.add({
						type: 'error',
						message: registrationErrorMessage,
					});
				} else {
					globalAlerts.add({
						type: 'success',
						message: t.registrationSuccess,
					});
				}

				clearState();
				history.push('/');
			} else {
				alerts.set({ type: 'error', message: registrationErrorMessage });
			}
		} catch (error) {
			if (error instanceof Error && error.message.includes('reCAPTCHA')) {
				alerts.set({ type: 'error', message: recaptchaUnavailableMessage });
			} else {
				alerts.set({ type: 'error', message: registrationErrorMessage });
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<Main>
			<div id="create-account-page">
				<Layout type="two-pane">
					<div>
						<h1>{t.welcomeCreateAccount}</h1>
						<p>{t.registrationIsSimple}</p>
						<p>
							<em>
								<strong>{t.note}</strong>: {t.accountRegistrationBeforeBuying}
							</em>
						</p>
					</div>
					<ContentCard>
						<h2>{t.fillinYourInfo}</h2>
						<AlertMessages alerts={alerts} />
						<form method="post" onSubmit={handleFormSubmit} data-action="registerAccount">
							<TextInput
								type="email"
								id="registration-email"
								autocomplete="email"
								value={email}
								onFocus={() => {
									// this field gets auto focused and blur'd on mount so `hasMounted` is neeed to check if user actually has focused the field
									if (hasMounted) setFocused('email');
								}}
								onBlur={() => {
									if (focused === 'email') {
										setTouched({ ...touched, email: true });
									}
								}}
								onChange={(e: ChangeEvent<HTMLInputElement>) => {
									setFocused('email');
									setEmail(e.target.value);
								}}
								placeholder={t.emailPlaceholder}
								autoFocus
								onKeyDown={onKeyPress}
								nextElement="new-password"
							/>
							{(submitted || touched.email) && !isEmailValid(email) && (
								<div>
									<span className="input-alert error">{t.emailSeemsInvalid}</span>
								</div>
							)}
							<br />
							<TextInput
								type="password"
								id="new-password"
								autocomplete="new-password"
								placeholder={t.password}
								value={newPassword}
								isValid={longEnough(newPassword)}
								onFocus={() => setFocused('password')}
								onBlur={() => setTouched({ ...touched, password: true })}
								onChange={(e) =>
									onChange({
										...passwordModel,
										newPassword: e.target.value,
										isValid: isValid(e.target.value, newPassword2),
									})
								}
								onKeyDown={onKeyPress}
								nextElement="confirm-password"
							/>
							{(submitted || touched.password) && !longEnough(newPassword) && (
								<div>
									<span className="input-alert error">{t.tooFewChars}</span>
								</div>
							)}
							<br />
							<TextInput
								type="password"
								id="confirm-password"
								placeholder={t.confirmPassword}
								value={newPassword2}
								isValid={confirmed(newPassword, newPassword2)}
								onFocus={() => setFocused('confirm')}
								onBlur={() => setTouched({ ...touched, confirm: true })}
								onChange={(e) =>
									onChange({
										...passwordModel,
										newPassword2: e.target.value,
										isValid: isValid(newPassword, e.target.value),
									})
								}
								onKeyDown={onKeyPress}
							/>
							{(touched.confirm || submitted) && !confirmed(newPassword, newPassword2) && (
								<div>
									<span className="input-alert error">{t.passwordMismatch}</span>
								</div>
							)}
							<br />
							<p className="register-account-text">
								{t.byRegisteringAnAccount}{' '}
								<Link to={tRoutes.purchaseTerms} target="_blank">
									{t.purchaseTerms}
								</Link>{' '}
								{t.andOur}{' '}
								<Link to={tRoutes.privacyPolicy} target="_blank">
									{t.privacyPolicy}
								</Link>
								{t.to}.
							</p>
							<Button disabled={loading || recaptchaScriptStatus !== 'ready'}>
								{loading ? t.loading : t.register}
							</Button>
						</form>
					</ContentCard>
				</Layout>
			</div>
		</Main>
	);
};

export const createAccountRouting: Routing = {
	path(urls) {
		return urls.createAccount;
	},
	component: CreateAccountPage,
	requiresAuth: false,
};
